import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { RoutingPaths } from 'app/routes/routes.ts'

import { FavoritesSuccessModal } from 'pages/avia/favorites/components/favorites-success-modal'
import { FavoritesToast } from 'pages/avia/favorites/components/favorites-toast'
import { FavoritesRequestBody } from 'pages/avia/favorites/hooks/types.ts'

import { favoritesStore } from 'widgets/avia/favorites'

import listTicketsStore from 'entities/ticket/models/list-tickets/list-tickets-store.ts'
import { prepareDataForFavorite } from 'entities/ticket/ui/card-of-ticket/ui/libs/prepareDataForFavorite.ts'
import { setSettingForApp } from 'entities/user/module/app-settings'
import userStore from 'entities/user/module/user-store'

import { checkUserAuth } from 'shared/lib/checkUserAuth.ts'
import { Props as CheckUserAuthProps } from 'shared/lib/checkUserAuth.ts'
import { getApiErrorMessage } from 'shared/lib/errorText'
import { useDialogBottomSheet } from 'shared/ui/dialog-bottom-sheet/lib'
import { message } from 'shared/ui/message'

interface PropsUseFavorites extends CheckUserAuthProps {
  favoritesSearchKey?: string
  favoritesParams: FavoritesRequestBody
}

export const useFavorites = ({
  favoritesSearchKey,
  favoritesParams,
  ...propsCheckUserAuth
}: PropsUseFavorites): {
  onAddFavorite: () => Promise<void>
  onRemoveFavorite: () => Promise<void>
} => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { openDialog } = useDialogBottomSheet()

  const { params } = favoritesParams

  const onAddFavorite = async () => {
    if (!favoritesSearchKey || !params) return

    const preparedData = prepareDataForFavorite({
      favoritesSearchKey,
      favoritesParams,
    })

    const alreadyShown = userStore.getClientStateString('aviaFavorite')

    try {
      await checkUserAuth(propsCheckUserAuth)
      await favoritesStore.addFavorite(preparedData)

      listTicketsStore.setTicketFavoriteByKey(favoritesSearchKey, true)

      if (
        favoritesSearchKey ===
        favoritesStore.favoritesDetail?.favorite_search_key
      ) {
        favoritesStore.setIsNotFavorite(true)
      }

      if (!alreadyShown) {
        void setSettingForApp({ key: 'aviaFavorite', value: 'true' })

        openDialog({
          child: <FavoritesSuccessModal />,
          backgroundColor: 'var(--white)',
          isClickable: false,
        })
      } else {
        message.success(
          <FavoritesToast
            text={t('favorites_block.add_to_favorites')}
            onGoToFavorites={() => navigate(RoutingPaths.avia.favorites.path())}
          />,
        )
      }
    } catch (e) {
      const errorMessage = getApiErrorMessage(e)
      if (errorMessage) message.error(errorMessage)
      throw e
    }
  }

  const onRemoveFavorite = async () => {
    if (!favoritesSearchKey || !params) return

    try {
      await checkUserAuth(propsCheckUserAuth)
      await favoritesStore.removeFavorite(favoritesSearchKey, params)
      listTicketsStore.setTicketFavoriteByKey(favoritesSearchKey, false)

      if (
        favoritesSearchKey ===
        favoritesStore.favoritesDetail?.favorite_search_key
      ) {
        favoritesStore.setIsNotFavorite(false)
      }

      message.success(
        <FavoritesToast text={t('favorites_block.remove_from_favorites')} />,
      )
    } catch (e) {
      const errorMessage = getApiErrorMessage(e)
      if (errorMessage) message.error(errorMessage)
      throw e
    }
  }

  return {
    onAddFavorite,
    onRemoveFavorite,
  }
}
